import React, {FC, useEffect, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {
  TrackDataEvent,
  useDataEventTracking,
} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {DataEventTrackingProvider} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getUserGivenName} from '../../../store/data/teammates/teammatesSelectors';
import {OnboardingGuideAnimatedStyles} from '../guide/onboardingGuidePopover';
import {FluSetupGuideTasksAndProgress} from '../tasks/onboardingTypes';
import {SetupGuideFrontLineUserResources} from './setupGuideFrontLineUserResources';
import {SetupGuideNavItem} from './setupGuideNavItem';
import {
  AnimatedSetupGuideScreen,
  SetupGuideBasicScreenProps,
  SetupGuidePopover,
  SetupGuideTasksScreenProps,
  useAutomaticallyAdvanceScreen,
  useRenderScreenTransitions,
} from './setupGuidePopover';
import {frontLineUserResourceTaskResources} from './setupGuideResourceTaskDefinitions';
import {SetupGuideTask} from './setupGuideTask';
import {FluSetupGuideConcreteScreen, FluSetupGuideScreensEnum} from './setupGuideTypes';

/*
 * Constants.
 */

const screens: ReadonlyArray<FluSetupGuideConcreteScreen> = [
  FluSetupGuideScreensEnum.GET_STARTED,
  FluSetupGuideScreensEnum.MORE,
];

/*
 * Props.
 */

interface FluSetupGuidePopoverProps {
  tasks: FluSetupGuideTasksAndProgress;
  onClose: () => void;
  defaultScreen: FluSetupGuideConcreteScreen;
  onDismissPermanentlyClick: () => void;
  panelId: AppPanelId;
  styles: OnboardingGuideAnimatedStyles | undefined;
}

/*
 * Component.
 */

export const FluSetupGuidePopover: FC<FluSetupGuidePopoverProps> = (props) => {
  const {onDismissPermanentlyClick, onClose, defaultScreen, panelId, styles, tasks} = props;
  const [currentScreen, setCurrentScreen] = useState<FluSetupGuideConcreteScreen>(defaultScreen);

  const {trackDataEvent} = useDataEventTracking();

  const userGivenName = useAppSelector(getUserGivenName);

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_SCREEN, {
      active_screen: currentScreen,
    });
  }, [trackDataEvent, currentScreen]);

  useAutomaticallyAdvanceFluScreen(tasks, defaultScreen, currentScreen, setCurrentScreen);

  const renderScreenTransitions = useRenderScreenTransitions<FluSetupGuideConcreteScreen>(
    screens,
    currentScreen,
  );

  const onExit = () => {
    onClose();
  };

  return (
    <DataEventTrackingProvider context={{name: 'flu_setup_guide', payload: {}}}>
      <SetupGuidePopover
        panelId={panelId}
        onDismissPermanentlyClick={onDismissPermanentlyClick}
        onClose={onExit}
        styles={styles}
        navMenu={
          <>
            {screens.map((navItemScreen) =>
              renderSetupGuideNavItem(props, navItemScreen, currentScreen, setCurrentScreen, trackDataEvent),
            )}
          </>
        }
      >
        {renderScreenTransitions((style, transitionScreen) =>
          renderScreen(props, transitionScreen, style, userGivenName, onClose),
        )}
      </SetupGuidePopover>
    </DataEventTrackingProvider>
  );
};

const GetStartedScreen: FC<SetupGuideTasksScreenProps> = (props) => (
  <AnimatedSetupGuideScreen
    style={props.style}
    title={<Trans id="setup-guide-front-line-user-get-started-screen-title">Get started</Trans>}
    progress={props.tasks.progress}
  >
    {props.tasks.tasks.map((t, i) => (
      <SetupGuideTask
        key={t.key}
        number={i + 1}
        title={t.title}
        description={t.description}
        details={t.details}
        isExternal={t.isExternal}
        isComplete={t.isComplete}
        startTask={t.startTask}
        panelId={props.panelId}
        onInternalTaskStart={props.onInternalTaskStart}
      />
    ))}
  </AnimatedSetupGuideScreen>
);

const MoreScreen: FC<SetupGuideBasicScreenProps> = (props) => (
  <AnimatedSetupGuideScreen
    style={props.style}
    title={<Trans id="setup-guide-more-screen-title">Get more out of Front</Trans>}
  >
    <SetupGuideFrontLineUserResources resources={frontLineUserResourceTaskResources} />
  </AnimatedSetupGuideScreen>
);

/*
 * Hooks.
 */

function useAutomaticallyAdvanceFluScreen(
  tasks: FluSetupGuideTasksAndProgress,
  defaultScreen: FluSetupGuideConcreteScreen,
  currentScreen: FluSetupGuideConcreteScreen,
  onScreenChange: (screen: FluSetupGuideConcreteScreen) => void,
) {
  const previousTasks = usePrevious(tasks);

  const didCompleteStartTasks =
    previousTasks?.start.progress.hasCompletedAllTasks === false && tasks.start.progress.hasCompletedAllTasks;

  const screenTransition: [FluSetupGuideConcreteScreen, FluSetupGuideConcreteScreen] | undefined =
    didCompleteStartTasks ? [FluSetupGuideScreensEnum.GET_STARTED, FluSetupGuideScreensEnum.MORE] : undefined;

  useAutomaticallyAdvanceScreen<FluSetupGuideConcreteScreen>(
    defaultScreen,
    currentScreen,
    screenTransition,
    onScreenChange,
  );
}

/*
 * Helpers.
 */

function renderSetupGuideNavItem(
  props: FluSetupGuidePopoverProps,
  navItemScreen: FluSetupGuideConcreteScreen,
  currentScreen: FluSetupGuideConcreteScreen,
  setCurrentScreen: (currentScreen: FluSetupGuideConcreteScreen) => void,
  trackDataEvent: TrackDataEvent,
) {
  const {tasks} = props;
  switch (navItemScreen) {
    case FluSetupGuideScreensEnum.GET_STARTED:
      return (
        <SetupGuideNavItem
          key={navItemScreen}
          title={<Trans id="setup-guide-front-line-user-get-started">Get started</Trans>}
          isCompleted={tasks.start.progress.hasCompletedAllTasks}
          isActive={currentScreen === FluSetupGuideScreensEnum.GET_STARTED}
          onClick={() => {
            setCurrentScreen(FluSetupGuideScreensEnum.GET_STARTED);
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_STARTED_TAB,
            );
          }}
        />
      );

    case FluSetupGuideScreensEnum.MORE:
      return (
        <SetupGuideNavItem
          key={navItemScreen}
          title={<Trans id="setup-guide-front-line-user-get-more">Get more out of Front</Trans>}
          isActive={currentScreen === FluSetupGuideScreensEnum.MORE}
          onClick={() => {
            setCurrentScreen(FluSetupGuideScreensEnum.MORE);
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_MORE_TAB,
            );
          }}
        />
      );

    default:
      throw new UnreachableError(navItemScreen);
  }
}

function renderScreen(
  props: FluSetupGuidePopoverProps,
  currentScreen: FluSetupGuideConcreteScreen,
  style: OnboardingGuideAnimatedStyles,
  userGivenName: string,
  onClose: () => void,
) {
  switch (currentScreen) {
    case FluSetupGuideScreensEnum.GET_STARTED:
      return (
        <GetStartedScreen
          style={style}
          tasks={props.tasks.start}
          onInternalTaskStart={onClose}
          userGivenName={userGivenName}
          panelId={props.panelId}
        />
      );
    case FluSetupGuideScreensEnum.MORE:
      return <MoreScreen style={style} onInternalTaskStart={onClose} />;

    default:
      throw new UnreachableError(currentScreen);
  }
}
